import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { LineChart } from '@mui/x-charts/LineChart';
import { useTheme } from '@mui/material/styles';
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge';
import type { VibrationSummary } from './types';

const SERIES_COLORS = {
  zrv1: '#1D9E75',
  xrv1: '#378ADD',
  zpv1: '#BA7517',
  xpv1: '#D4537E',
};

const LEGEND_ITEMS = [
  { key: 'zrv1', label: 'Z-Axis RMS',  color: SERIES_COLORS.zrv1 },
  { key: 'xrv1', label: 'X-Axis RMS',  color: SERIES_COLORS.xrv1 },
  { key: 'zpv1', label: 'Z-Axis Peak', color: SERIES_COLORS.zpv1 },
  { key: 'xpv1', label: 'X-Axis Peak', color: SERIES_COLORS.xpv1 },
];

interface Props {
  vibration: VibrationSummary;
}

export default function VibrationPanel({ vibration }: Props) {
  const theme     = useTheme();
  const tickColor = theme.palette.text.secondary;
  const { hasData, gauges, series } = vibration;

  return (
    <Box style={{ background: 'var(--color-bg-secondary)', borderRadius: 12, padding: '1.1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Typography style={{ fontFamily: "'Syne', sans-serif", fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>Vibration Monitoring</Typography>

      {!hasData ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, py: 4 }}>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: 'var(--color-text-secondary)' }}>
            No vibration data for this period
          </Typography>
        </Box>
      ) : (
        <>
          {/* Gauges */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 1 }}>
            {gauges.map(gauge => (
              <Box key={gauge.label} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 110, height: 110 }}>
                  <Gauge
                    value={gauge.value}
                    valueMin={0}
                    valueMax={gauge.max}
                    cornerRadius="50%"
                    sx={{
                      [`& .${gaugeClasses.valueText}`]: {
                        fontSize: 14,
                        fontFamily: "'DM Mono', monospace",
                        fill: 'var(--color-text-primary)',
                      },
                      [`& .${gaugeClasses.valueArc}`]: {
                        fill: gauge.statusColor,
                      },
                      [`& .${gaugeClasses.referenceArc}`]: {
                        fill: 'rgba(128,128,128,0.15)',
                      },
                    }}
                    text={`${gauge.value.toFixed(4)}`}
                  />
                </Box>
                <Typography sx={{ fontFamily: "'Syne', sans-serif", fontSize: 11, fontWeight: 600, color: 'var(--color-text-primary)', textAlign: 'center' }}>
                  {gauge.label}
                </Typography>
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', px: 1, py: 0.2,
                  borderRadius: '4px', background: `${gauge.statusColor}25`,
                  border: `1px solid ${gauge.statusColor}`,
                }}>
                  <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: 10, fontWeight: 600, color: gauge.statusColor }}>
                    {gauge.status}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Legend */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 0.5, justifyContent: 'center' }}>
            {LEGEND_ITEMS.map(item => (
              <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '2px', background: item.color, flexShrink: 0 }} />
                <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: 'var(--color-text-secondary)' }}>
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Line chart */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <LineChart
            xAxis={[{
              data:           series.labels.map((_, i) => i),
              valueFormatter: (i: number, ctx) => {
                if (ctx.location === 'tooltip') {
                  const raw = series.buckets[i];
                  if (raw) return new Date(raw).toLocaleString('en-GB', {
                    weekday: 'short', day: 'numeric', month: 'short',
                    hour: '2-digit', minute: '2-digit',
                  });
                }
                return series.labels[i] ?? '';
              },
              tickLabelStyle: { fontSize: 10, fill: tickColor },
              scaleType:      'point',
            }]}
            yAxis={[{
              tickLabelStyle: { fontSize: 10, fill: tickColor },
              label:          'mm/s',
            }]}
            series={[
              { id: 'zrv1', label: 'Z-Axis RMS',  data: series.zrv1, color: SERIES_COLORS.zrv1, showMark: false, curve: 'catmullRom' },
              { id: 'xrv1', label: 'X-Axis RMS',  data: series.xrv1, color: SERIES_COLORS.xrv1, showMark: false, curve: 'catmullRom' },
              { id: 'zpv1', label: 'Z-Axis Peak', data: series.zpv1, color: SERIES_COLORS.zpv1, showMark: false, curve: 'catmullRom' },
              { id: 'xpv1', label: 'X-Axis Peak', data: series.xpv1, color: SERIES_COLORS.xpv1, showMark: false, curve: 'catmullRom' },
            ]}
            grid={{ horizontal: true }}
            height={180}
            margin={{ top: 8, right: 16, bottom: 30, left: 44 }}
            slotProps={{ legend: { hidden: true } }}
            slots={{ legend: () => null }}
            sx={{
              '& .MuiMarkElement-root': { outline: 'none' },
              '& .MuiChartsLegend-root': { display: 'none' },
            }}
          />
          </Box>
        </>
      )}
    </Box>
  );
}

const styles = {
  panel: {
    background:    'var(--color-bg-secondary)',
    borderRadius:  3,
    p:             '1.1rem 1.25rem',
    display:       'flex',
    flexDirection: 'column' as const,
    gap:           1,
  },
  title: {
    fontSize:   13,
    fontWeight: 600,
    color:      'var(--color-text-primary)',
    mb:         '2px',
  },
  sub: {
    fontFamily: "'DM Mono', monospace",
    fontSize:   11,
    color:      'var(--color-text-secondary)',
    mb:         0.5,
  },
};
